import { useState } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import first from 'lodash/first';
import { useTranslation } from 'react-i18next';

import {
  AmplitudeProductEvent,
  buildSearchResultClickProperties,
} from '@cfa/pathway-fe-shared';
import {
  type ContentReference,
  searchClickFeedbackMutation,
} from '@cfa/pathway-fe-shared/xp';
import { Typography } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';
import useExploreSearch from '@/hooks/useExploreSearch';
import { useScrollMemory } from '@/hooks/useScrollMemory';
import { incrementResourceClicksAtom } from '@/hooks/useSearchSessionTracker';
import { useSmartSearchEnabled } from '@/hooks/useSmartSearchEnabled';

import {
  consolidateSearchDocuments,
  countSearchRowsForResourceId,
  getResourceIdsWithDuplicateSearchRows,
  searchRowsToChooserReferences,
} from '@/utils/consolidateSearchDocuments';
import type { DocumentRouteState } from '@/utils/document-utils';
import { isApiError } from '@/utils/request';
import { hasNoSpecialCharacters } from '@/utils/string';
import { cn } from '@/utils/tailwind';

import DocumentHead from '@/components/document/DocumentHead';
import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants';

import SearchbarSubheader from '../Layout/Subheader/SearchbarSubheader';

import SearchResultCard, {
  SearchResultCardProps,
} from './components/SearchResultCard';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { clearDocumentAtom, setDocumentAtom } from '@/atoms/document';
import { userPrefsAtom } from '@/atoms/userPrefs';

const buildExploreSearchDocumentSearch = (
  pathname: string,
  search: string,
  promptChooseVersion: boolean,
): string => {
  const params = new URLSearchParams({
    from: 'search',
    backUrl: `${pathname}${search}`,
  });
  if (promptChooseVersion) {
    params.set('chooseVersion', 'true');
  }
  return `?${params.toString()}`;
};

export const SearchResultsPage = () => {
  'use memo';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const query = new URLSearchParams(search).get('query') ?? '';
  const setDocument = useSetAtom(setDocumentAtom);
  const clearDocument = useSetAtom(clearDocumentAtom);
  const incrementResourceClicks = useSetAtom(incrementResourceClicksAtom);
  const { country: userCountry } = useAtomValue(userPrefsAtom);

  const addSearchResultFeedback = useMutation(searchClickFeedbackMutation());

  const [activeThumbsUp, setActiveThumbsUp] = useState<string[]>([]);
  const [activeThumbsDown, setActiveThumbsDown] = useState<string[]>([]);

  const { searchResultFeedback: featureFlagSearchResultFeedback } = useFlags();

  const { data: auth } = useAuth();
  const isStaff = auth?.isStaff ?? false;
  const consolidateSearch =
    isStaff ||
    auth?.audience === Constants.USER_AUDIENCE.ADMIN ||
    auth?.audience === Constants.USER_AUDIENCE.CONTRACTOR;
  const { data, isFetching, error } = useExploreSearch(query);

  const rawPageResults =
    data?.clientRawPageResults ??
    data?.rawFirstPageResults ??
    data?.results ??
    [];
  const searchResults = consolidateSearch
    ? consolidateSearchDocuments(rawPageResults)
    : rawPageResults;
  const duplicateSearchResourceIds =
    getResourceIdsWithDuplicateSearchRows(rawPageResults);

  const misspellingSuggestion = data
    ? (first(data.spellingSuggestions) ?? '')
    : '';
  const queryId = data?.queryId;
  const totalResults = data?.totalResults ?? 0;
  const visibleResultCount = searchResults.length;

  const track = useAmplitudeTrack();
  const smartSearchEnabled = useSmartSearchEnabled();

  const scrollMemoryKey = `search:${query}`;

  const [isNavigating, setIsNavigating] = useState(false);

  useScrollMemory({
    container: 'body',
    key: scrollMemoryKey,
    enabled: !isFetching && !isNavigating,
  });

  const onSearchResultFeedback = ({
    resultId,
    resultQueryId,
  }: {
    resultId?: string;
    resultQueryId?: string;
  }) => {
    if (!resultQueryId || !resultId) {
      datadogRum.addError(
        new Error(
          `No ${
            !resultQueryId ? 'queryId' : 'resultId'
          } found in SearchResultsV2`,
        ),
      );
      return;
    }

    const resp = addSearchResultFeedback.mutateAsync({
      query: {
        queryId: resultQueryId,
        resultId,
      },
    });

    resp.catch((err: unknown) => {
      const errorDetails = {
        error: JSON.stringify(err),
        query,
        queryId: resultQueryId,
        resultId,
      };
      datadogRum.addError(
        new Error(
          `Error in addSearchResultFeedback: ${JSON.stringify(errorDetails)}`,
        ),
      );
    });
  };

  const goToDetailsPage = (id: string, promptChooseVersion: boolean) => {
    setIsNavigating(true);
    const state: DocumentRouteState | undefined = promptChooseVersion
      ? {
          searchReferences: searchRowsToChooserReferences(
            rawPageResults,
            id,
            userCountry.id as ContentReference['country'],
          ),
        }
      : undefined;
    navigate(
      {
        pathname: `/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/${id}`,
        search: buildExploreSearchDocumentSearch(
          pathname,
          search,
          promptChooseVersion,
        ),
      },
      { state },
    );
  };

  const onSearchCardClick: SearchResultCardProps['onClick'] = ({
    resultId,
    documentId,
  }) => {
    incrementResourceClicks();
    const resultIndex = searchResults.findIndex(
      row =>
        row.searchId === resultId || (!row.searchId && row.id === documentId),
    );
    const doc = resultIndex !== -1 ? searchResults[resultIndex] : undefined;

    const trackingPayload = buildSearchResultClickProperties({
      query: query || 'empty',
      num_results: totalResults,
      document_id: documentId,
      smart_search_enabled: smartSearchEnabled,
      ...(resultIndex >= 0 ? { result_position: resultIndex + 1 } : {}),
    });

    if (resultIndex < 0) {
      datadogRum.addError(
        new Error(
          `Clicked documentId not found in searchResults: ${documentId}`,
        ),
      );
    }

    track(AmplitudeProductEvent.SEARCH_RESULT_CLICK, { ...trackingPayload });

    const promptChooseVersion =
      consolidateSearch &&
      (duplicateSearchResourceIds.includes(documentId) ||
        countSearchRowsForResourceId(rawPageResults, documentId) > 1);

    if (promptChooseVersion) {
      clearDocument();
    } else if (doc) {
      setDocument({
        document: doc,
        icon: doc.icon,
      });
    }

    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
    goToDetailsPage(documentId, promptChooseVersion);
  };

  const getFeedbackText = (id?: string) => {
    const isThumbsUp = !!id && activeThumbsUp.includes(id);
    const isThumbsDown = !!id && activeThumbsDown.includes(id);
    const feedbackGiven = isThumbsUp || isThumbsDown;

    return (
      <span
        className={cn(
          feedbackGiven && isThumbsUp && 'text-feedback-success-base',
          feedbackGiven && isThumbsDown && 'text-feedback-error-base',
        )}
      >
        {feedbackGiven
          ? t('Search.submitFeedback')
          : `${t('Search.rateResult')}:`}
      </span>
    );
  };

  const onThumbsUp: SearchResultCardProps['onThumbsUp'] = (id, resultId) => {
    setActiveThumbsUp(prev => [...prev, id]);
    setActiveThumbsDown(prev => prev.filter(item => id !== item));
    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
  };

  const onThumbsDown: SearchResultCardProps['onThumbsDown'] = (
    id,
    resultId,
  ) => {
    setActiveThumbsUp(prev => prev.filter(item => id !== item));
    setActiveThumbsDown(prev => [...prev, id]);
    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
  };

  const onSearchMisspellingSuggestionClick = (suggestion: string) => {
    track(AmplitudeProductEvent.SEARCH_SUGGESTION_CLICKED, {
      suggestion,
      originalQuery: query,
      originalSearchQueryId: queryId,
    });
    setIsNavigating(true);
    navigate({
      pathname: `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`,
      search: `query=${suggestion}`,
    });
  };

  if (isApiError(error)) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  const searchResultsCountHeader = data ? (
    <Typography data-testid="ExploreSearchResultsCount" variant="body1">
      {visibleResultCount} {t('Search.numberOfResultsFor')} <b>{query}</b>
    </Typography>
  ) : null;

  return (
    <>
      <SearchbarSubheader />
      <div className="relative mt-8">
        <LoadingOverlay isOpen={isFetching && !(searchResults.length > 0)} />
        <DocumentHead pageTitle={t('Search.pageTitle', 'Search Results')} />
        {(!!searchResults.length || misspellingSuggestion) && !isFetching ? (
          <>
            {misspellingSuggestion && (
              <span
                className="text-grayscale-gray7 mb-4 block font-normal"
                data-testid="MisspellingSuggestionWrapper"
              >
                {t('Search.showingResultsSuggestion')}
                {': '}
                <button
                  className="text-brand-navy! dark:text-contrast! cursor-pointer font-bold!"
                  data-testid="MisspellingSuggestion"
                  onClick={() =>
                    onSearchMisspellingSuggestionClick(misspellingSuggestion)
                  }
                  type="button"
                >
                  {misspellingSuggestion}
                </button>
              </span>
            )}
            {searchResultsCountHeader}
          </>
        ) : data ? (
          searchResultsCountHeader
        ) : null}
        <div>
          {!searchResults.length ? (
            <div className="px-5 py-7">{t('Search.noResults')}</div>
          ) : (
            searchResults.map(searchResultDocument => {
              const {
                icon,
                id,
                contentApiUrl,
                contentUrl,
                lastModified,
                name,
                otherContentUrl,
                searchId,
                sourceSystem,
                type,
              } = searchResultDocument;

              const isTridion = sourceSystem === 'TRIDION';
              const docUseContentUrl =
                isTridion ||
                sourceSystem ===
                  Constants.EXPANDED_FILE_FORMAT_TYPES.GAME.toUpperCase() ||
                sourceSystem ===
                  Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE.toUpperCase();
              const documentId = id ?? '';
              if (!documentId) {
                return null;
              }

              const baseUrl = docUseContentUrl
                ? (contentUrl ?? contentApiUrl ?? '')
                : (contentApiUrl ?? contentUrl ?? '');
              const translatedUrl = otherContentUrl ?? '';
              const documentUrl = {
                base: baseUrl,
                translated: translatedUrl,
              };

              const newResultId = searchId || documentId;
              const documentType = type ?? '';
              const iconName = icon ?? '';
              const safeIcon =
                iconName && hasNoSpecialCharacters(iconName)
                  ? iconName
                  : 'placeholder';
              const modifiedDate = lastModified
                ? new Date(lastModified).toLocaleDateString()
                : '';
              const documentName = name ?? '';

              return (
                <SearchResultCard
                  documentId={documentId}
                  documentType={documentType}
                  featureFlagSearchResultFeedback={
                    featureFlagSearchResultFeedback
                  }
                  getFeedbackText={getFeedbackText}
                  icon={safeIcon}
                  id={newResultId}
                  isMarketTest={false}
                  isThumbsDown={activeThumbsDown.includes(newResultId)}
                  isThumbsUp={activeThumbsUp.includes(newResultId)}
                  isTridion={isTridion}
                  key={newResultId}
                  lastModified={modifiedDate}
                  locationTypes={[]}
                  name={documentName}
                  onClick={onSearchCardClick}
                  onThumbsDown={onThumbsDown}
                  onThumbsUp={onThumbsUp}
                  resultId={newResultId}
                  url={documentUrl}
                />
              );
            })
          )}
        </div>
        <div className="h-[400px]" />
      </div>
    </>
  );
};

export default SearchResultsPage;
